"use client";

import { DEFAULT_PHONE_COUNTRY } from "@klarify/constants";
import { AsYouType } from "libphonenumber-js";
import type * as React from "react";
import { useLayoutEffect, useRef } from "react";
import { Input, type InputProps } from "./input";

export const formatPhoneAsYouType = (input: string): string => {
	const trimmed = input.trim();
	if (!trimmed) {
		return "";
	}
	// AsYouType returns "" for a lone "+"; preserve it so the next digit can route into a country code.
	if (trimmed === "+") {
		return "+";
	}
	return new AsYouType(DEFAULT_PHONE_COUNTRY).input(input);
};

const ALLOWED_CHARS_REGEX = /^[\d+\s\-()]+$/;

const isDigit = (ch: string | undefined) =>
	ch !== undefined && ch >= "0" && ch <= "9";

const countDigits = (s: string, end: number): number => {
	let n = 0;
	for (let i = 0; i < end && i < s.length; i++) {
		if (isDigit(s[i])) {
			n++;
		}
	}
	return n;
};

const indexAfterNthDigit = (formatted: string, n: number): number => {
	if (n <= 0) {
		return 0;
	}
	let count = 0;
	for (let i = 0; i < formatted.length; i++) {
		if (isDigit(formatted[i])) {
			count++;
			if (count === n) {
				return i + 1;
			}
		}
	}
	return formatted.length;
};

export interface PhoneInputProps
	extends Omit<InputProps, "value" | "onChange" | "type" | "onBeforeInput"> {
	value: string;
	onChange: (value: string) => void;
}

export const PhoneInput = ({
	ref,
	value,
	onChange,
	inputMode = "tel",
	autoComplete = "tel",
	...rest
}: PhoneInputProps) => {
	const internalRef = useRef<HTMLInputElement>(null);
	const inputRef =
		(ref as React.RefObject<HTMLInputElement | null> | undefined) ??
		internalRef;

	// Cursor index restored after React renders the formatted value.
	const pendingCursorRef = useRef<number | null>(null);

	useLayoutEffect(() => {
		if (pendingCursorRef.current !== null && inputRef.current) {
			const pos = pendingCursorRef.current;
			inputRef.current.setSelectionRange(pos, pos);
			pendingCursorRef.current = null;
		}
	});

	const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
		// `data` is the inserted text — null for deletions / IME compose, which we let through.
		const data = (e.nativeEvent as InputEvent).data;
		if (data && !ALLOWED_CHARS_REGEX.test(data)) {
			e.preventDefault();
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value;
		const cursorPos = e.target.selectionStart ?? raw.length;
		const digitsLeftOfCursor = countDigits(raw, cursorPos);

		const formatted = formatPhoneAsYouType(raw);
		pendingCursorRef.current = indexAfterNthDigit(
			formatted,
			digitsLeftOfCursor,
		);

		onChange(formatted);
	};

	return (
		<Input
			ref={inputRef}
			type="tel"
			inputMode={inputMode}
			autoComplete={autoComplete}
			value={value}
			onChange={handleChange}
			onBeforeInput={handleBeforeInput}
			{...rest}
		/>
	);
};
